import { Notify } from "@/components/ui/notify";
import { Pill } from "@/components/ui/pill";
import { useBatchCharacteristics } from "@/features/organization/batch-characteristics/hooks";
import type { BatchCharacteristicResponse } from "@/types/batch-characteristics.types";
import type { ReportFilter } from "@/types/production-report.types";

interface Props {
  characteristicId: string | null;
  value: string | null;
  compareBy: string | null;
  /** O recorte como o servidor o resolveu — é ele que diz o que ficou de fora. */
  filter: ReportFilter | null;
  onChange: (patch: Record<string, string | null>) => void;
}

/**
 * Spec 084 (FR-014 · FR-017a) — o RECORTE por um valor e a característica do QUADRO.
 *
 * "Não informado" e "misto" são opções de propósito: "quanto rendeu o material que ninguém marcou"
 * é uma pergunta legítima, e some da tela se ela só oferecer os valores declarados.
 *
 * O recorte e o quadro são eixos distintos — o recorte estreita o relatório inteiro, o quadro abre
 * uma característica em colunas sobre o período todo — então a mesma característica não serve aos
 * dois ao mesmo tempo, e a segunda lista a esconde.
 */
export function CharacteristicFilter({
  characteristicId,
  value,
  compareBy,
  filter,
  onChange,
}: Props) {
  const query = useBatchCharacteristics();
  const defs = query.data ?? [];
  // Falha de leitura NÃO vira "esta organização não declarou nenhuma característica": sumir com o
  // seletor faria o recorte parecer inexistente, e o gestor concluiria que a tela não o oferece.
  if (query.isError) {
    return (
      <Notify.QueryError
        error={query.error}
        onRetry={() => void query.refetch()}
        variant="inline"
      />
    );
  }
  if (defs.length === 0) return null;
  const atual = defs.find((d) => d.id === characteristicId) ?? null;

  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-[11px] text-text-secondary">
        Recortar por
        <select
          className="rounded-md border border-border-default bg-bg-surface px-2 py-1 text-[12px] text-text-primary"
          value={characteristicId ?? ""}
          onChange={(e) =>
            // Trocar a característica zera o valor: o valor antigo não existe na nova.
            onChange({ char: e.target.value || null, value: null })
          }
        >
          <option value="">Sem recorte</option>
          {defs
            .filter((d) => d.id !== compareBy)
            .map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
        </select>
      </label>

      {atual ? (
        <label className="flex flex-col gap-1 text-[11px] text-text-secondary">
          Valor
          <select
            className="rounded-md border border-border-default bg-bg-surface px-2 py-1 text-[12px] text-text-primary"
            value={value ?? ""}
            onChange={(e) => onChange({ value: e.target.value || null })}
          >
            <option value="">Escolha um valor</option>
            {opcoesDe(atual).map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
            <option value="NONE">não informado</option>
            <option value="MIXED">misto</option>
          </select>
        </label>
      ) : null}

      <label className="flex flex-col gap-1 text-[11px] text-text-secondary">
        Comparar por
        <select
          className="rounded-md border border-border-default bg-bg-surface px-2 py-1 text-[12px] text-text-primary"
          value={compareBy ?? ""}
          onChange={(e) => onChange({ compare: e.target.value || null })}
        >
          <option value="">Sem quadro</option>
          {defs
            .filter((d) => d.id !== characteristicId)
            .map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
        </select>
      </label>

      {filter ? (
        <div className="flex flex-wrap items-center gap-2 pb-1">
          <Pill tone="info" withDot>
            {filter.characteristicName}: {filter.valueLabel}
          </Pill>
          <span className="text-[11px] text-text-tertiary">{excluidasLabel(filter)}</span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * As opções de uma característica. A de texto livre não tem lista — o recorte por um texto exato
 * chega pela URL (o assistente monta, o quadro leva), e oferecer um campo aberto aqui convidaria a
 * digitar um valor que não existe e receber uma tela vazia sem explicação.
 */
function opcoesDe(def: BatchCharacteristicResponse): Array<{ key: string; label: string }> {
  return def.values.map((v) => ({ key: `v:${v.id}`, label: v.label }));
}

/** O que o recorte deixou de fora, em texto — nunca um número solto sem substantivo. */
function excluidasLabel(filter: ReportFilter): string {
  if (filter.markedRunsInPeriod === 0) {
    return "nenhuma batelada marcada neste período";
  }
  const partes = [
    filter.excluded.noneRuns > 0 ? `${filter.excluded.noneRuns} sem valor` : null,
    filter.excluded.mixedRuns > 0 ? `${filter.excluded.mixedRuns} mista(s)` : null,
  ].filter(Boolean);
  return partes.length > 0 ? `fora do recorte: ${partes.join(" · ")}` : "nada ficou de fora";
}
